import { useEffect, useId, useState } from 'react';
import { toast } from 'sonner';
import { Button, Input, FilterChip } from '@indipia/ui-core';
import { createCustomer, createPartner, createVendor } from './api';
import type { RecipientType, SaveDialogItem } from './types';
import { useWorkTypes } from '../hooks/useWorkTypes';

const SECTION_LABEL: Record<RecipientType, string> = {
  CUSTOMER: '고객',
  PARTNER: '반장님',
  VENDOR: '거래처',
};

const SECTION_TYPES: RecipientType[] = ['CUSTOMER', 'PARTNER', 'VENDOR'];

interface SaveAfterSendDialogProps {
  items: SaveDialogItem[];
  onChange: (items: SaveDialogItem[]) => void;
  onClose: () => void;
}

export function SaveAfterSendDialog({ items, onChange, onClose }: SaveAfterSendDialogProps) {
  const [saving, setSaving] = useState(false);
  const [vendorNameErrors, setVendorNameErrors] = useState<Record<string, string>>({});
  const [specialtyErrors, setSpecialtyErrors] = useState<Record<string, string>>({});
  const [dealItemErrors, setDealItemErrors] = useState<Record<string, string>>({});
  // 전문 분야·거래 품목은 필수 입력이라 기본 펼침 상태로 시작한다.
  const [sectionOpen, setSectionOpen] = useState<Set<string>>(() => new Set(items.map((it) => it.key)));
  const workTypes = useWorkTypes();
  const titleId = useId();

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  function toggleSection(key: string) {
    setSectionOpen((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }

  function update(key: string, patch: Partial<SaveDialogItem>) {
    onChange(items.map((it) => (it.key === key ? { ...it, ...patch } : it)));
  }

  function validateRequiredFields(): boolean {
    const vErrs: Record<string, string> = {};
    const sErrs: Record<string, string> = {};
    const dErrs: Record<string, string> = {};
    items.forEach((it) => {
      if (it.excluded) return;
      if (it.recipientType === 'VENDOR') {
        if (!it.vendorName.trim()) vErrs[it.key] = '거래처명을 입력해 주세요.';
        if (it.dealItems.length === 0) dErrs[it.key] = '거래 품목을 선택해 주세요.';
      } else if (it.recipientType === 'PARTNER') {
        if (it.specialtyTypes.length === 0) sErrs[it.key] = '전문 분야를 선택해 주세요.';
      }
    });
    setVendorNameErrors(vErrs);
    setSpecialtyErrors(sErrs);
    setDealItemErrors(dErrs);

    const errorKeys = [...Object.keys(sErrs), ...Object.keys(dErrs)];
    if (errorKeys.length > 0) {
      setSectionOpen((prev) => new Set([...prev, ...errorKeys]));
    }

    return Object.keys(vErrs).length === 0 && Object.keys(sErrs).length === 0 && Object.keys(dErrs).length === 0;
  }

  async function handleSaveAll() {
    if (!validateRequiredFields()) return;

    setSaving(true);
    const toSave = items.filter((it) => !it.excluded);
    let successCount = 0;
    const failedKeys = new Set<string>();

    await Promise.allSettled(
      toSave.map(async (it) => {
        try {
          if (it.recipientType === 'CUSTOMER') {
            await createCustomer(it.name, it.mobile);
          } else if (it.recipientType === 'PARTNER') {
            const specialtyStr = it.specialtyTypes.join(',') || undefined;
            await createPartner(it.name, it.mobile, specialtyStr);
          } else {
            const dealItemStr = it.dealItems.join(',') || undefined;
            await createVendor(it.vendorName.trim(), it.name, it.mobile, dealItemStr);
          }
          successCount++;
        } catch {
          failedKeys.add(it.key);
        }
      }),
    );

    setSaving(false);

    if (failedKeys.size === 0) {
      toast.success(`${successCount}명의 정보가 저장되었습니다.`);
      onClose();
    } else {
      toast.error(`${failedKeys.size}명 저장 실패`);
      // 성공한 항목 제거, 실패한 항목만 남김
      onChange(items.filter((it) => failedKeys.has(it.key)));
    }
  }

  const activeCount = items.filter((it) => !it.excluded).length;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div
        className="relative z-10 flex max-h-[80vh] w-full max-w-xl flex-col rounded-t-xxl bg-surface-raised p-6 shadow-xl sm:rounded-xxl"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        {/* 헤더 */}
        <h2 id={titleId} className="text-base font-semibold text-text-primary">
          신규 수신자 정보를 저장하시겠습니까?
        </h2>
        <p className="mt-1 text-sm text-text-secondary">
          처음 메시지를 보낸 분들의 정보를 저장하면 다음에 더 빠르게 보낼 수 있어요.
        </p>

        {/* 수신자 섹션 목록 */}
        <div className="mt-4 flex-1 space-y-4 overflow-y-auto">
          {SECTION_TYPES.map((type) => {
            const group = items.filter((it) => it.recipientType === type);
            if (group.length === 0) return null;
            return (
              <div key={type}>
                <p className="mb-2 text-xs font-semibold text-text-secondary">
                  {SECTION_LABEL[type]}
                </p>
                <div className="space-y-2">
                  {group.map((item) => (
                    <div
                      key={item.key}
                      className={`space-y-2 rounded-xxl border border-border-default p-3 transition-opacity ${
                        item.excluded ? 'opacity-40' : ''
                      }`}
                    >
                      {/* 거래처명 입력 (VENDOR 전용) */}
                      {type === 'VENDOR' && !item.excluded && (
                        <Input
                          label="거래처명 (필수)"
                          id={`${item.key}-vendorName`}
                          type="text"
                          value={item.vendorName}
                          onChange={(e) => {
                            update(item.key, { vendorName: e.target.value });
                            setVendorNameErrors((prev) => ({ ...prev, [item.key]: '' }));
                          }}
                          placeholder="거래처명을 입력해 주세요"
                          error={vendorNameErrors[item.key] || undefined}
                          className="max-w-none"
                        />
                      )}

                      {/* 이름·연락처 (읽기전용) */}
                      <div className="flex gap-3">
                        <Input
                          label={type === 'VENDOR' ? '담당자명' : '이름'}
                          id={`${item.key}-name`}
                          readOnly
                          value={item.name}
                          className="flex-1 max-w-none"
                        />
                        <Input
                          label="연락처"
                          id={`${item.key}-mobile`}
                          readOnly
                          value={item.mobile}
                          className="flex-1 max-w-none"
                        />
                      </div>

                      {/* 전문 분야 (PARTNER 전용, 필수) — 토글 */}
                      {type === 'PARTNER' && !item.excluded && (
                        <div>
                          <button
                            type="button"
                            onClick={() => toggleSection(item.key)}
                            className="flex w-full items-center justify-between text-xs text-text-secondary"
                          >
                            <span>
                              전문 분야 <span className="text-error">*</span>
                              {item.specialtyTypes.length > 0 && (
                                <span className="ml-1.5 text-primary-500">
                                  {item.specialtyTypes.length}개 선택
                                </span>
                              )}
                            </span>
                            <span className="text-xs">
                              {sectionOpen.has(item.key) ? '▲' : '▼'}
                            </span>
                          </button>
                          {sectionOpen.has(item.key) && (
                            <div className="mt-1.5 flex flex-wrap gap-1.5">
                              {workTypes.map((wt) => {
                                const selected = item.specialtyTypes.includes(wt.code);
                                return (
                                  <FilterChip
                                    key={wt.code}
                                    active={selected}
                                    onClick={() => {
                                      const next = selected
                                        ? item.specialtyTypes.filter((c) => c !== wt.code)
                                        : [...item.specialtyTypes, wt.code];
                                      update(item.key, { specialtyTypes: next });
                                      setSpecialtyErrors((prev) => ({ ...prev, [item.key]: '' }));
                                    }}
                                  >
                                    {wt.codeName}
                                  </FilterChip>
                                );
                              })}
                            </div>
                          )}
                          {specialtyErrors[item.key] && (
                            <p className="mt-1 text-xs text-error">{specialtyErrors[item.key]}</p>
                          )}
                        </div>
                      )}

                      {/* 거래 품목 (VENDOR 전용, 필수) — 토글 */}
                      {type === 'VENDOR' && !item.excluded && (
                        <div>
                          <button
                            type="button"
                            onClick={() => toggleSection(item.key)}
                            className="flex w-full items-center justify-between text-xs text-text-secondary"
                          >
                            <span>
                              거래 품목 <span className="text-error">*</span>
                              {item.dealItems.length > 0 && (
                                <span className="ml-1.5 text-primary-500">
                                  {item.dealItems.length}개 선택
                                </span>
                              )}
                            </span>
                            <span className="text-xs">
                              {sectionOpen.has(item.key) ? '▲' : '▼'}
                            </span>
                          </button>
                          {sectionOpen.has(item.key) && (
                            <div className="mt-1.5 flex flex-wrap gap-1.5">
                              {workTypes.map((wt) => {
                                const selected = item.dealItems.includes(wt.code);
                                return (
                                  <FilterChip
                                    key={wt.code}
                                    active={selected}
                                    onClick={() => {
                                      const next = selected
                                        ? item.dealItems.filter((c) => c !== wt.code)
                                        : [...item.dealItems, wt.code];
                                      update(item.key, { dealItems: next });
                                      setDealItemErrors((prev) => ({ ...prev, [item.key]: '' }));
                                    }}
                                  >
                                    {wt.codeName}
                                  </FilterChip>
                                );
                              })}
                            </div>
                          )}
                          {dealItemErrors[item.key] && (
                            <p className="mt-1 text-xs text-error">{dealItemErrors[item.key]}</p>
                          )}
                        </div>
                      )}

                      {/* 저장 제외/포함 토글 */}
                      <div className="flex justify-end">
                        <button
                          type="button"
                          onClick={() => update(item.key, { excluded: !item.excluded })}
                          className="text-xs text-text-secondary hover:text-error"
                        >
                          {item.excluded ? '저장 포함' : '저장 제외'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* 액션 버튼 */}
        <div className="mt-4 flex gap-2">
          <Button variant="secondary" type="button" onClick={onClose} className="flex-1">
            닫기
          </Button>
          <Button
            variant="primary"
            type="button"
            onClick={() => {
              void handleSaveAll();
            }}
            disabled={saving || activeCount === 0}
            loading={saving}
            className="flex-1"
          >
            {saving ? '저장 중…' : '전체 저장'}
          </Button>
        </div>
      </div>
    </div>
  );
}
